/**
 * Wizard di prima configurazione del sistema ERP.
 *
 * Guida l'utente attraverso 5 step:
 * 1. Dati azienda (nome, P.IVA, sede)
 * 2. Primo amministratore (email, nome, password)
 * 3. Rami operativi (crea 1+ UO)
 * 4. Moduli per ramo (checklist)
 * 5. Riepilogo e conferma
 *
 * Route: /setup (pubblica, accessibile solo se sistema non configurato)
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface RamoConfig {
  nome: string;
  tipo: string;
  moduli: string[];
}

const TIPI_RAMO = [
  'sede_principale',
  'filiale',
  'divisione',
  'reparto',
  'magazzino',
  'negozio',
  'ufficio',
  'altro',
];

const SetupWizard: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Dati azienda
  const [tenantNome, setTenantNome] = useState('');
  const [tipoSoggetto, setTipoSoggetto] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [sedeIndirizzo, setSedeIndirizzo] = useState('');
  const [sedeCitta, setSedeCitta] = useState('');
  const [sedeProvincia, setSedeProvincia] = useState('');
  const [sedeCap, setSedeCap] = useState('');

  // Step 2: Admin
  const [adminEmail, setAdminEmail] = useState('');
  const [adminNome, setAdminNome] = useState('');
  const [adminCognome, setAdminCognome] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');

  // Step 3: Rami
  const [rami, setRami] = useState<RamoConfig[]>([]);
  const [newRamoNome, setNewRamoNome] = useState('');
  const [newRamoTipo, setNewRamoTipo] = useState('divisione');

  // Step 4: moduli disponibili
  const [moduliDisponibili] = useState<string[]>([
    'documentale', 'magazzino', 'cassa', 'ordini', 'prezzi', 'banca',
  ]);

  // Verifica che il sistema non sia gia configurato
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    const check = async () => {
      try {
        // Se /setup restituisce 409, il sistema e gia configurato
        const res = await fetch('/api/v1/setup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_nome: '__check__',
            email: 'check@check.it',
            nome: 'c', cognome: 'c', password: 'c',
          }),
        });
        if (res.status === 409) {
          navigate('/login', { replace: true });
          return;
        }
      } catch { /* rete down — mostra wizard */ }
      setChecking(false);
    };
    void check();
  }, [navigate]);

  const aggiungiRamo = () => {
    if (!newRamoNome.trim()) return;
    setRami([...rami, { nome: newRamoNome.trim(), tipo: newRamoTipo, moduli: [] }]);
    setNewRamoNome('');
  };

  const rimuoviRamo = (idx: number) => {
    setRami(rami.filter((_, i) => i !== idx));
  };

  const toggleModulo = (ramoIdx: number, modulo: string) => {
    setRami(rami.map((r, i) => {
      if (i !== ramoIdx) return r;
      const moduli = r.moduli.includes(modulo)
        ? r.moduli.filter((m) => m !== modulo)
        : [...r.moduli, modulo];
      return { ...r, moduli };
    }));
  };

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/setup/wizard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_nome: tenantNome,
          tipo_soggetto: tipoSoggetto || null,
          partita_iva: partitaIva || null,
          codice_fiscale: codiceFiscale || null,
          sede_indirizzo: sedeIndirizzo || null,
          sede_citta: sedeCitta || null,
          sede_provincia: sedeProvincia || null,
          sede_cap: sedeCap || null,
          email: adminEmail,
          nome: adminNome,
          cognome: adminCognome,
          password: adminPassword,
          rami: rami.map((r) => ({
            nome: r.nome,
            tipo: r.tipo,
            moduli: r.moduli,
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail ?? `Errore ${res.status}`);
      }

      // Login automatico
      const loginRes = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          username: adminEmail,
          password: adminPassword,
        }),
      });
      if (loginRes.ok) {
        const loginData = await loginRes.json();
        localStorage.setItem('token', loginData.access_token);
      }

      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSubmitting(false);
    }
  }, [
    tenantNome, tipoSoggetto, partitaIva, codiceFiscale,
    sedeIndirizzo, sedeCitta, sedeProvincia, sedeCap,
    adminEmail, adminNome, adminCognome, adminPassword,
    rami, navigate,
  ]);

  if (checking) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Verifica stato sistema...</p>
      </div>
    );
  }

  const canNext = () => {
    switch (step) {
      case 1: return tenantNome.trim().length > 0;
      case 2: return (
        adminEmail.trim().length > 0 &&
        adminNome.trim().length > 0 &&
        adminCognome.trim().length > 0 &&
        adminPassword.length >= 8 &&
        adminPassword === adminPasswordConfirm
      );
      case 3: return true; // rami opzionali
      case 4: return true;
      default: return true;
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-semibold">Configurazione iniziale</h1>
          <p className="mt-2 text-muted-foreground">
            Step {step} di 5
          </p>
          {/* Progress bar */}
          <div className="mx-auto mt-4 flex max-w-md gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className="h-1.5 flex-1 rounded-full"
                style={{
                  backgroundColor: s <= step ? '#1B3A6B' : '#e5e7eb',
                }}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Step 1: Dati azienda */}
        {step === 1 && (
          <Card>
            <CardHeader><CardTitle>Dati azienda</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome azienda *</label>
                <Input value={tenantNome} onChange={(e) => setTenantNome(e.target.value)} placeholder="Ragione sociale" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium">Tipo soggetto</label>
                  <select
                    value={tipoSoggetto}
                    onChange={(e) => setTipoSoggetto(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Seleziona...</option>
                    <option value="societa_capitali">Societa di capitali</option>
                    <option value="societa_persone">Societa di persone</option>
                    <option value="ditta_individuale">Ditta individuale</option>
                    <option value="professionista">Professionista</option>
                    <option value="associazione">Associazione</option>
                    <option value="cooperativa">Cooperativa</option>
                    <option value="ente_pubblico">Ente pubblico</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Partita IVA</label>
                  <Input value={partitaIva} onChange={(e) => setPartitaIva(e.target.value)} placeholder="01234567890" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Codice fiscale</label>
                <Input value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Sede legale</label>
                <div className="grid grid-cols-[1fr_100px] gap-2">
                  <Input value={sedeIndirizzo} onChange={(e) => setSedeIndirizzo(e.target.value)} placeholder="Indirizzo" />
                  <Input value={sedeCap} onChange={(e) => setSedeCap(e.target.value)} placeholder="CAP" />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Input value={sedeCitta} onChange={(e) => setSedeCitta(e.target.value)} placeholder="Comune" />
                  <Input value={sedeProvincia} onChange={(e) => setSedeProvincia(e.target.value)} placeholder="Provincia (sigla)" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Amministratore */}
        {step === 2 && (
          <Card>
            <CardHeader><CardTitle>Amministratore</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium">Nome *</label>
                  <Input value={adminNome} onChange={(e) => setAdminNome(e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Cognome *</label>
                  <Input value={adminCognome} onChange={(e) => setAdminCognome(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Email *</label>
                <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium">Password * (min 8 caratteri)</label>
                  <Input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Conferma password *</label>
                  <Input
                    type="password"
                    value={adminPasswordConfirm}
                    onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                  />
                  {adminPasswordConfirm && adminPassword !== adminPasswordConfirm && (
                    <p className="mt-1 text-xs text-destructive">Le password non corrispondono</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Rami operativi */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Rami operativi</CardTitle>
              <p className="text-sm text-muted-foreground">
                Crea i rami della tua attivita (es. divisioni, sedi, reparti). Opzionale — puoi aggiungerli dopo.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {rami.map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <span className="font-medium">{r.nome}</span>
                    <span className="ml-2 text-sm text-muted-foreground">({r.tipo})</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => rimuoviRamo(i)} className="text-destructive">
                    Rimuovi
                  </Button>
                </div>
              ))}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-muted-foreground">Nome ramo</label>
                  <Input
                    value={newRamoNome}
                    onChange={(e) => setNewRamoNome(e.target.value)}
                    placeholder="es. Sede Milano, Divisione IT..."
                    onKeyDown={(e) => e.key === 'Enter' && aggiungiRamo()}
                  />
                </div>
                <div style={{ width: 160 }}>
                  <label className="mb-1 block text-xs text-muted-foreground">Tipo</label>
                  <select
                    value={newRamoTipo}
                    onChange={(e) => setNewRamoTipo(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    {TIPI_RAMO.map((t) => (
                      <option key={t} value={t}>{t.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={aggiungiRamo} disabled={!newRamoNome.trim()}>Aggiungi</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Moduli per ramo */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Moduli per ramo</CardTitle>
              <p className="text-sm text-muted-foreground">
                Seleziona i moduli da attivare per ogni ramo. Potrai modificarli in seguito.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {rami.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nessun ramo configurato. Puoi attivare i moduli dopo dalla pagina Gestione moduli.
                </p>
              ) : (
                rami.map((ramo, ramoIdx) => (
                  <div key={ramoIdx}>
                    <h3 className="mb-2 font-medium">{ramo.nome}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {moduliDisponibili.map((m) => {
                        const isActive = ramo.moduli.includes(m);
                        return (
                          <button
                            key={m}
                            onClick={() => toggleModulo(ramoIdx, m)}
                            className="flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors"
                            style={{
                              backgroundColor: isActive ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                              borderColor: isActive ? '#22c55e' : undefined,
                            }}
                          >
                            <div
                              className="h-3 w-3 rounded-sm border"
                              style={{
                                backgroundColor: isActive ? '#22c55e' : 'transparent',
                                borderColor: isActive ? '#22c55e' : '#d1d5db',
                              }}
                            />
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 5: Riepilogo */}
        {step === 5 && (
          <Card>
            <CardHeader><CardTitle>Riepilogo</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Azienda</h3>
                <p className="font-medium">{tenantNome}</p>
                {partitaIva && <p className="text-sm">P.IVA: {partitaIva}</p>}
                {sedeCitta && <p className="text-sm">{sedeIndirizzo}, {sedeCap} {sedeCitta} ({sedeProvincia})</p>}
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Amministratore</h3>
                <p className="text-sm">{adminNome} {adminCognome} — {adminEmail}</p>
              </div>
              {rami.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Rami operativi</h3>
                  {rami.map((r, i) => (
                    <p key={i} className="text-sm">
                      {r.nome} ({r.tipo}) — {r.moduli.length > 0 ? r.moduli.join(', ') : 'nessun modulo'}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigazione */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
          >
            Indietro
          </Button>
          {step < 5 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()}>
              Avanti
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Creazione in corso...' : 'Completa configurazione'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
